/**
 * MiniModal panel identity heading.
 *
 * Distinct from {@link SectionLabel}: a centered title with a short mint
 * trail whose width tracks the title length, instead of hairline rules
 * around a capsule.
 */

import React, { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../theme/ColorPalette";

/** Trail width as a fraction of the measured title width. */
const TRAIL_WIDTH_RATIO = 0.55;

/** Floor so short titles still keep a readable mint mark. */
const TRAIL_MIN_WIDTH_PX = 44;

interface MiniModalTitleProps {

    /** Visible panel name. */
    "title": string;

    /** When `true`, use the dark overlay palette. */
    "isDarkUi": boolean;

    /** When `true`, play the entrance of the mint trail. */
    "isEntered": boolean;
}

/**
 * Panel heading with a centered title and a proportional mint trail underneath.
 *
 * @param props Title, theme, and entrance state.
 * @returns Header chrome for a MiniModal.
 */
const MiniModalTitle: React.FC<MiniModalTitleProps> = ( {
    title,
    isDarkUi,
    isEntered,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const reducedMotion = useReducedMotion();
    const titleRef = useRef<HTMLHeadingElement | null>( null );
    const [ titleWidthPx, setTitleWidthPx ] = useState( 0 );
    const showTrail = ( reducedMotion || isEntered ) && titleWidthPx > 0;
    const mint = palette.brandPrimary;
    const trailWidthPx = Math.max( TRAIL_MIN_WIDTH_PX, Math.round( titleWidthPx * TRAIL_WIDTH_RATIO ) );

    const washStyle = useMemo( (): CSSProperties => ( { "background": `linear-gradient(180deg, ${ mint }1F 0%, transparent 100%)` } ), [ mint ] );

    const trailStyle = useMemo( (): CSSProperties => ( {
        "width":      showTrail ? `${ trailWidthPx }px` : "0",
        "background": `linear-gradient(90deg, ${ mint }00 0%, ${ mint } 50%, ${ mint }00 100%)`,
        "transition": reducedMotion
            ? "none"
            : "width 420ms cubic-bezier(0.2, 0.8, 0.2, 1)",
    } ), [ mint, reducedMotion, showTrail, trailWidthPx ] );

    useEffect( () => {
        const titleElement = titleRef.current;
        if ( titleElement === null || typeof ResizeObserver === "undefined" ) {
            return;
        }

        /**
         * Syncs the trail to the rendered title box (truncate included).
         */
        const syncTitleWidth = (): void => {
            setTitleWidthPx( titleElement.getBoundingClientRect().width );
        };

        syncTitleWidth();
        const observer = new ResizeObserver( syncTitleWidth );
        observer.observe( titleElement );

        return () => {
            observer.disconnect();
        };
    }, [ title ] );

    return (
        <header className="relative overflow-hidden px-3.5 pb-1 pt-3.5">
            <div
                aria-hidden={true}
                className="pointer-events-none absolute inset-x-0 top-0 h-11"
                style={washStyle}
            />
            <div className="relative flex flex-col items-center">
                <h2
                    className="w-fit max-w-full truncate text-center text-[13px] font-semibold tracking-[0.08em]"
                    ref={titleRef}
                    style={{ "color": palette.overlayTextPrimary }}
                >
                    {title}
                </h2>
                <span
                    aria-hidden={true}
                    className="mt-1.5 block h-[2px] rounded-full"
                    style={trailStyle}
                />
            </div>
        </header>
    );
};

export default MiniModalTitle;
